<template>
  <div
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    style="background: rgba(0, 0, 0, 0.8); z-index: 9999;"
  >
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content bg-dark text-light retention-duplicate-modal">
        <div class="modal-header border-bottom border-secondary">
          <h5 class="modal-title">
            <i class="fas fa-globe mr-2"></i>
            Duplicate "{{ baseMessage.name || '(unnamed)' }}" to other languages
          </h5>
          <button type="button" class="close text-light" :disabled="isBusy" @click="closeModal">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <!-- Phase 1: locale selection -->
        <div v-if="phase === 'select'" class="modal-body">
          <p class="intro">
            New messages will be created as <strong>drafts</strong>. Submit them to Apple individually after review.
          </p>
          <p class="source-line">
            Source language:
            <span v-if="baseLanguageLabel" class="source-tag">{{ baseLanguageLabel }} — {{ baseMessage.language }}</span>
            <span v-else class="muted">auto-detect from message content</span>
          </p>

          <div class="quick-selects">
            <button class="qs-btn" type="button" @click="selectAll">Select all</button>
            <button class="qs-btn" type="button" @click="clearAll">Clear</button>
            <button class="qs-btn" type="button" @click="selectTopTier">Top tier</button>
            <span class="qs-count">{{ selected.size }} / {{ availableLocales.length }} selected</span>
          </div>

          <div class="locale-grid">
            <label
              v-for="loc in availableLocales"
              :key="loc.code"
              class="locale-row"
              :class="{ disabled: isSourceLocale(loc.code) }"
            >
              <input
                type="checkbox"
                :checked="selected.has(loc.code)"
                :disabled="isSourceLocale(loc.code)"
                @change="toggle(loc.code, $event)"
              />
              <span class="locale-label">{{ loc.label }}</span>
              <span class="locale-code">{{ loc.code }}</span>
            </label>
          </div>

          <p class="char-limit-note">
            <i class="fas fa-info-circle"></i>
            Apple limits header to 66 chars and body to 144 chars. Translations exceeding either will be skipped.
          </p>
        </div>

        <!-- Phase 2: translating spinner -->
        <div v-else-if="phase === 'translating'" class="modal-body translating-pane">
          <div class="spinner-border text-info mb-3" role="status"></div>
          <p>Translating to {{ inFlightCount }} languages…</p>
          <p class="muted small">This usually takes 5–20 seconds. Please don't close this dialog.</p>
        </div>

        <!-- Phase 2b: shortening spinner (AI re-pass) -->
        <div v-else-if="phase === 'shortening'" class="modal-body translating-pane">
          <div class="spinner-border text-info mb-3" role="status"></div>
          <p>Asking AI to shorten {{ inFlightCount }} translation{{ inFlightCount === 1 ? '' : 's' }}…</p>
          <p class="muted small">This usually takes 5–20 seconds. Please don't close this dialog.</p>
        </div>

        <!-- Phase 3: result -->
        <div v-else class="modal-body result-pane">
          <div v-if="result.created.length > 0" class="result-section is-created">
            <h6><i class="fas fa-check-circle"></i> {{ result.created.length }} draft{{ result.created.length === 1 ? '' : 's' }} created</h6>
            <ul>
              <li v-for="draft in result.created" :key="draft.id">
                <code>{{ draft.name }}</code>
                <span class="muted">— {{ getLocaleLabel(draft.language) }}</span>
              </li>
            </ul>
          </div>

          <div v-if="result.failures.length > 0" class="result-section is-failed">
            <h6>
              <i class="fas fa-exclamation-triangle"></i>
              {{ result.failures.length }} failed — retryable
            </h6>
            <ul>
              <li v-for="f in result.failures" :key="`fail-${f.language}`">
                <strong>{{ getLocaleLabel(f.language) }}</strong>
                <span class="muted">— {{ f.message }}</span>
              </li>
            </ul>
            <button class="btn btn-sm btn-warning" type="button" @click="retryFailed">
              <i class="fas fa-redo"></i> Retry failed
            </button>
          </div>

          <div v-if="result.skipped.length > 0" class="result-section is-skipped">
            <h6>
              <i class="fas fa-info-circle"></i>
              {{ result.skipped.length }} skipped — exceeds Apple limits
            </h6>
            <p class="muted small">
              The AI translation produced strings longer than Apple allows. Try shortening with AI, or rewrite manually.
            </p>
            <ul>
              <li v-for="s in result.skipped" :key="`skip-${s.language}`">
                <strong>{{ getLocaleLabel(s.language) }}</strong>
                <span class="muted">— {{ s.reason }}</span>
              </li>
            </ul>
            <button
              class="btn btn-sm btn-info"
              type="button"
              :disabled="!canRetryShorten"
              @click="retryWithShorten"
            >
              <i class="fas fa-compress-alt"></i> Retry with shorten
            </button>
          </div>

          <div v-if="!result.created.length && !result.failures.length && !result.skipped.length" class="muted">
            No drafts created. Try again with different selections.
          </div>
        </div>

        <div class="modal-footer border-top border-secondary">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="isBusy"
            @click="closeModal"
          >
            {{ phase === 'result' ? 'Close' : 'Cancel' }}
          </button>
          <button
            v-if="phase === 'select'"
            type="button"
            class="btn btn-primary"
            :disabled="selected.size === 0"
            @click="submit"
          >
            Duplicate to {{ selected.size }} language{{ selected.size === 1 ? '' : 's' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SUPPORTED_LOCALES } from '../../constants/retentionLocales';
import notify from '../../util/notification';

const TOP_TIER_CODES = [
  'en-US', 'en-GB', 'es-ES', 'es-MX', 'fr-FR',
  'de-DE', 'ja', 'ko', 'zh-Hans', 'pt-BR'
];

const LOCALE_LABEL_BY_CODE = SUPPORTED_LOCALES.reduce((acc, l) => {
  acc[l.code] = l.label;
  return acc;
}, {});

export default {
  name: 'RetentionDuplicateLanguagesModal',
  props: {
    baseMessage: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      phase: 'select',
      selected: new Set(),
      inFlightCount: 0,
      result: { created: [], failures: [], skipped: [] },
      availableLocales: SUPPORTED_LOCALES
    };
  },
  computed: {
    baseLanguageLabel() {
      return this.baseMessage.language ? LOCALE_LABEL_BY_CODE[this.baseMessage.language] : null;
    },
    isBusy() {
      return this.phase === 'translating' || this.phase === 'shortening';
    },
    canRetryShorten() {
      // Backend ships translatedContent on every skipped item; this guard is
      // defense-in-depth in case some entry came in via a path that didn't
      // populate it (e.g. a future "missing fields" skip).
      return this.result.skipped.length > 0
        && this.result.skipped.every((s) => s.translatedContent && typeof s.translatedContent === 'object');
    }
  },
  methods: {
    isSourceLocale(code) {
      return this.baseMessage.language === code;
    },
    toggle(code, event) {
      const next = new Set(this.selected);
      if (event.target.checked) next.add(code);
      else next.delete(code);
      this.selected = next;
    },
    selectAll() {
      const next = new Set();
      for (const loc of this.availableLocales) {
        if (!this.isSourceLocale(loc.code)) next.add(loc.code);
      }
      this.selected = next;
    },
    clearAll() {
      this.selected = new Set();
    },
    selectTopTier() {
      const next = new Set();
      for (const code of TOP_TIER_CODES) {
        if (!this.isSourceLocale(code)) next.add(code);
      }
      this.selected = next;
    },
    getLocaleLabel(code) {
      return LOCALE_LABEL_BY_CODE[code] || code;
    },
    async submit() {
      const targetLanguages = Array.from(this.selected);
      if (targetLanguages.length === 0) return;
      this.inFlightCount = targetLanguages.length;
      this.phase = 'translating';
      try {
        const result = await this.$store.dispatch('retention/duplicateMessageToLanguages', {
          messageId: this.baseMessage.id,
          targetLanguages
        });
        this.result = {
          created: result.created || [],
          failures: result.failures || [],
          skipped: result.skipped || []
        };
        this.phase = 'result';
        if (this.result.created.length > 0) {
          notify(
            `${this.result.created.length} draft${this.result.created.length === 1 ? '' : 's'} created.`,
            'success'
          );
        }
      } catch (err) {
        // Action already surfaces a toast; bounce back to selection so user can retry.
        console.error(err);
        this.phase = 'select';
      }
    },
    async retryFailed() {
      const retryLanguages = this.result.failures.map((f) => f.language);
      if (retryLanguages.length === 0) return;
      // Drop previous failures and skipped from result; keep created so user
      // sees the cumulative success list across attempts.
      const keepCreated = [...this.result.created];
      this.inFlightCount = retryLanguages.length;
      this.phase = 'translating';
      try {
        const result = await this.$store.dispatch('retention/duplicateMessageToLanguages', {
          messageId: this.baseMessage.id,
          targetLanguages: retryLanguages
        });
        this.result = {
          created: [...keepCreated, ...(result.created || [])],
          failures: result.failures || [],
          skipped: [...this.result.skipped, ...(result.skipped || [])]
        };
        this.phase = 'result';
      } catch (err) {
        console.error(err);
        this.phase = 'result';
      }
    },
    async retryWithShorten() {
      if (!this.canRetryShorten) return;
      const items = this.result.skipped.map((s) => ({
        language: s.language,
        translatedContent: s.translatedContent
      }));
      // Keep current `created` and `failures`; replace `skipped` with whatever
      // comes back (some will become `created`, some still skipped, some may
      // turn into `failures` if the localizer errors out).
      const keepCreated = [...this.result.created];
      const keepFailures = [...this.result.failures];
      this.inFlightCount = items.length;
      this.phase = 'shortening';
      try {
        const result = await this.$store.dispatch('retention/shortenSkippedTranslations', {
          messageId: this.baseMessage.id,
          items
        });
        this.result = {
          created: [...keepCreated, ...(result.created || [])],
          failures: [...keepFailures, ...(result.failures || [])],
          skipped: result.skipped || []
        };
        this.phase = 'result';
      } catch (err) {
        console.error(err);
        this.phase = 'result';
      }
    },
    closeModal() {
      if (this.isBusy) return;
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/*
 * The panel's black-dashboard.css ships hard-coded rules
 *   `.modal-content .modal-body p { color: rgb(34, 42, 66); }`         (0,2,1)
 *   `.modal-content .modal-header .modal-title { color: rgb(34, 42, 66); }`  (0,3,0)
 *   `.modal-header { padding-bottom: 0 }` / `.modal-footer { padding-top: 0 }`
 * Prefixing every selector below with `.retention-duplicate-modal` bumps
 * specificity to (0,3,0)+ so our colors and spacing win.
 */
.retention-duplicate-modal .modal-header {
  padding: 1rem 1.25rem;
}

.retention-duplicate-modal .modal-body {
  padding: 1.25rem;
}

.retention-duplicate-modal .modal-footer {
  padding: 1rem 1.25rem;
}

.retention-duplicate-modal .modal-header .modal-title {
  font-size: 1.05rem;
  color: #fff;
}

.retention-duplicate-modal .intro {
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.88rem;
  margin-bottom: 0.65rem;
}

.retention-duplicate-modal .source-line {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 1.5rem;
}

.retention-duplicate-modal .source-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(3, 179, 218, 0.15);
  color: #6dd0ea;
  font-size: 0.78rem;
  margin-left: 0.4rem;
}

.retention-duplicate-modal .muted {
  color: rgba(255, 255, 255, 0.4);
}

.retention-duplicate-modal .small {
  font-size: 0.8rem;
}

.retention-duplicate-modal .quick-selects {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.retention-duplicate-modal .qs-btn {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.85);
  padding: 0.3rem 0.7rem;
  font-size: 0.78rem;
  border-radius: 6px;
  cursor: pointer;
}

.retention-duplicate-modal .qs-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.retention-duplicate-modal .qs-count {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
}

.retention-duplicate-modal .locale-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25rem 1rem;
  max-height: 280px;
  overflow-y: auto;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
}

.retention-duplicate-modal .locale-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.4rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  margin: 0;
}

.retention-duplicate-modal .locale-row:hover:not(.disabled) {
  background: rgba(255, 255, 255, 0.05);
}

.retention-duplicate-modal .locale-row.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.retention-duplicate-modal .locale-row input[type="checkbox"] {
  accent-color: #03B3DA;
  margin: 0;
}

.retention-duplicate-modal .locale-label {
  flex: 1;
  color: rgba(255, 255, 255, 0.88);
}

.retention-duplicate-modal .locale-code {
  color: rgba(255, 255, 255, 0.4);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.76rem;
}

.retention-duplicate-modal .char-limit-note {
  margin-top: 0.85rem;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 193, 7, 0.06);
  border-left: 2px solid rgba(255, 193, 7, 0.4);
  padding: 0.55rem 0.7rem;
  border-radius: 4px;
}

.retention-duplicate-modal .char-limit-note i {
  color: rgba(255, 193, 7, 0.7);
  margin-right: 0.35rem;
}

.retention-duplicate-modal .translating-pane {
  text-align: center;
  padding: 2.5rem 1rem;
}

.retention-duplicate-modal .translating-pane p {
  margin-bottom: 0.4rem;
  color: rgba(255, 255, 255, 0.85);
}

.retention-duplicate-modal .result-pane {
  max-height: 60vh;
  overflow-y: auto;
}

.retention-duplicate-modal .result-section {
  padding: 0.75rem 1rem;
  border-radius: 6px;
  margin-bottom: 0.85rem;
  border-left: 3px solid;
}

.retention-duplicate-modal .result-section h6 {
  margin: 0 0 0.5rem;
  font-size: 0.92rem;
  font-weight: 600;
}

.retention-duplicate-modal .result-section h6 i {
  margin-right: 0.4rem;
}

.retention-duplicate-modal .result-section ul {
  margin: 0.3rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.85rem;
}

.retention-duplicate-modal .result-section li {
  margin-bottom: 0.2rem;
  color: rgba(255, 255, 255, 0.85);
}

.retention-duplicate-modal .result-section code {
  background: rgba(255, 255, 255, 0.05);
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.92);
}

/*
 * Class names are deliberately prefixed with `is-` to avoid colliding with
 * black-dashboard's global utilities (`.info` is `display: none`, `.success`
 * and `.failure` similarly carry global side-effects). Without the prefix,
 * the bucket sections render in the DOM but stay invisible.
 */
.retention-duplicate-modal .result-section.is-created {
  background: rgba(46, 204, 113, 0.06);
  border-color: #2ecc71;
}

.retention-duplicate-modal .result-section.is-created h6 {
  color: #2ecc71;
}

.retention-duplicate-modal .result-section.is-failed {
  background: rgba(231, 76, 60, 0.06);
  border-color: #e74c3c;
}

.retention-duplicate-modal .result-section.is-failed h6 {
  color: #ff8a80;
}

.retention-duplicate-modal .result-section.is-failed .btn-warning {
  margin-top: 0.5rem;
  font-size: 0.78rem;
}

.retention-duplicate-modal .result-section.is-skipped {
  background: rgba(255, 193, 7, 0.06);
  border-color: #ffc107;
}

.retention-duplicate-modal .result-section.is-skipped h6 {
  color: #ffc107;
}

.retention-duplicate-modal .close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  opacity: 0.5;
  cursor: pointer;
  color: #fff;
}

.retention-duplicate-modal .close:hover:not(:disabled) {
  opacity: 0.85;
}

.retention-duplicate-modal .close:disabled {
  cursor: not-allowed;
}
</style>
